import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CardImage from '../../../ui/CardImage';
import { isLandscape } from '../../../ui/cardOrientation';
import { cardImageProps, otherFace } from '../utils/cardView';
import { hasCardValues } from '../utils/cardValues';
import CardValues from './CardValues';
import './FloatingCardPreview.css';

export const PREVIEW_WIDTH_PX = 280;
export const PREVIEW_GAP_PX = 24;
/** Werte-Panel rechts neben dem Bild; die Lage rechnet mit der Gesamtbreite. */
export const PREVIEW_VALUES_WIDTH_PX = 176;
export const PREVIEW_VALUES_GAP_PX = 8;
/** Reservierte Zeile unter dem Bild einer zweiseitigen Karte (Hinweis auf die angesehene Seite). */
export const PREVIEW_HINT_HEIGHT_PX = 24;
const PREVIEW_HEIGHT_PX = PREVIEW_WIDTH_PX * 1.4;
const VIEWPORT_MARGIN_PX = 8;

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/**
 * Linke obere Ecke der Vorschau in Client-Koordinaten.
 * - `auto`: rechts neben dem Zeiger mit `PREVIEW_GAP_PX` Abstand (reicht der Platz nicht: links
 *   davon), senkrecht mittig zum Zeiger.
 * - `above`: Unterkante `PREVIEW_GAP_PX` über `anchorRect`, waagerecht mittig zum Anker — über dem
 *   Handfächer verdeckt sie so die Nachbarkarten nicht.
 * Beides wird auf den Viewport geklemmt; ist über dem Anker zu wenig Platz, überdeckt die Vorschau
 * ihn lieber, als aus dem Bild zu ragen. `width`: Gesamtbreite samt Werte-Panel; `height`:
 * Gesamthöhe, also die Bildhöhe oder ein höheres Werte-Panel.
 */
export function computePreviewPosition({
  pointer, anchorRect, placement, viewport, width = PREVIEW_WIDTH_PX, height = PREVIEW_HEIGHT_PX,
}) {
  const maxLeft = Math.max(VIEWPORT_MARGIN_PX, viewport.width - width - VIEWPORT_MARGIN_PX);
  const maxTop = Math.max(VIEWPORT_MARGIN_PX, viewport.height - height - VIEWPORT_MARGIN_PX);

  if (placement === 'above' && anchorRect) {
    const centerX = anchorRect.left + anchorRect.width / 2;
    return {
      left: clamp(centerX - width / 2, VIEWPORT_MARGIN_PX, maxLeft),
      top: clamp(anchorRect.top - PREVIEW_GAP_PX - height, VIEWPORT_MARGIN_PX, maxTop),
    };
  }

  const origin = pointer || {
    x: anchorRect ? anchorRect.left + anchorRect.width : 0,
    y: anchorRect ? anchorRect.top + anchorRect.height / 2 : 0,
  };
  let left = origin.x + PREVIEW_GAP_PX;
  if (left + width > viewport.width - VIEWPORT_MARGIN_PX) {
    left = origin.x - PREVIEW_GAP_PX - width;
  }
  return {
    left: clamp(left, VIEWPORT_MARGIN_PX, maxLeft),
    top: clamp(origin.y - height / 2, VIEWPORT_MARGIN_PX, maxTop),
  };
}

/**
 * Schwebende Kartenvorschau des Playtest-Bretts, gespeist von `useHoverPreview`.
 *
 *   <FloatingCardPreview preview={hover.preview} />
 *
 * Kartenbild über `ui/CardImage`, 280 px breit (lesbar, `frontend/CLAUDE.md`), Lage nach
 * `computePreviewPosition`. `pointer-events: none`: die Vorschau fängt nie den Zeiger, sonst
 * löste sie über der Karte ein Verlassen und damit ihr eigenes Ausblenden aus. Gerendert per
 * Portal am `document.body`, weil die Brettfläche mit Kamera-Transform und Überlauf-Kappung
 * eine eigene Ebenen-Gruppe bildet. Verdeckte Karten zeigt sie mit ihrer Vorderseite — wer eine
 * fremde verdeckte Karte nicht sehen darf, bekommt vom Aufrufer gar keine Vorschau-Handler.
 * Das Bild bleibt frei von Overlays; Stärke/Widerstandskraft, Counter und Marken stehen, sofern
 * vorhanden, in einem Panel rechts daneben (`preview-values`, Spec §14). Ist das Panel höher als
 * das Bild (viele Marken), klemmt die Lage mit seiner gemessenen Höhe.
 *
 * Gezeigte Seite: ist `preview.otherSide` gesetzt und die Karte zweiseitig, die andere als die
 * echte (`otherFace`), sonst die echte; eine einseitige Karte zeigt immer ihre Vorderseite. Unter
 * dem Bild — nie darüber — steht bei jeder zweiseitigen Karte die Zeile `preview-hint`, bei
 * angesehener anderer Seite mit „Nur deine Ansicht" (andere Teilnehmer sehen die echte Seite). Der
 * Platz der Zeile ist auch ohne Text reserviert und in der Höhe der Lage enthalten, sonst
 * spränge die Vorschau beim Umschalten um eine Zeilenhöhe. Kein Umdreh-Knopf.
 *
 * Querformat (`scryfallData.imageOrientation`, Spec 2026-10-31 §6.2): liegt die angesehene Seite
 * quer, ist das Bild 392 × 280 px statt 280 × 392 (gleiche lange Kante), die Lage rechnet mit den
 * Quermaßen; maßgeblich ist die angesehene Seite inklusive `otherSide`, nicht die der Karte.
 * Fehlt das Feld (verdeckte fremde Karten), bleibt die Ansicht hochkant.
 */
export default function FloatingCardPreview({ preview }) {
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const card = preview?.card || null;
  useLayoutEffect(() => {
    const next = panelRef.current ? panelRef.current.getBoundingClientRect().height : 0;
    setPanelHeight((prev) => (prev === next ? prev : next));
  }, [card]);

  if (!card) return null;
  const hasValues = hasCardValues(card);
  const imageProps = cardImageProps(card);
  const hasBack = Boolean(imageProps.backSrc);
  const viewingOther = Boolean(preview.otherSide) && hasBack;
  const face = viewingOther ? otherFace(imageProps.face) : imageProps.face;
  // Querformat: maßgeblich ist die angesehene Seite, nicht die der Karte — ein Battle liegt vorn
  // quer (392 × 280, gleiche lange Kante), seine angesehene Rückseite hochkant.
  const landscape = isLandscape(imageProps.orientation, face);
  const imageWidth = landscape ? PREVIEW_HEIGHT_PX : PREVIEW_WIDTH_PX;
  const imageHeight = landscape ? PREVIEW_WIDTH_PX : PREVIEW_HEIGHT_PX;
  const width = imageWidth + (hasValues ? PREVIEW_VALUES_GAP_PX + PREVIEW_VALUES_WIDTH_PX : 0);
  const { left, top } = computePreviewPosition({
    ...preview,
    width,
    height: Math.max(imageHeight + (hasBack ? PREVIEW_HINT_HEIGHT_PX : 0), hasValues ? panelHeight : 0),
    viewport: { width: window.innerWidth, height: window.innerHeight },
  });

  return createPortal(
    <div
      className="floating-card-preview"
      style={{ left, top, width, gap: PREVIEW_VALUES_GAP_PX }}
      data-testid="floating-card-preview"
      aria-hidden
    >
      <div className="floating-card-preview__column" style={{ width: imageWidth }}>
        <div className="floating-card-preview__card">
          <CardImage {...imageProps} face={face} landscape="preview" />
        </div>
        {hasBack && (
          <div className="floating-card-preview__hint" data-testid="preview-hint">
            {viewingOther && <span className="floating-card-preview__hint-text">Nur deine Ansicht</span>}
          </div>
        )}
      </div>
      {hasValues && (
        <CardValues
          ref={panelRef}
          card={card}
          variant="panel"
          as="aside"
          className="floating-card-preview__values"
          style={{ width: PREVIEW_VALUES_WIDTH_PX }}
          data-testid="preview-values"
        />
      )}
    </div>,
    document.body
  );
}
